// Envelope helpers (contract 03 §6). Preserves v3 `ensureApiSuccess`: a `status:'error'`
// envelope throws an ApiError rather than returning a bad payload.
import { ApiError } from './ApiError'
import type { ApiEnvelope } from './types'

/** Enveloped user routes: read `res.data.data`; throw ApiError on a `status:'error'` envelope. */
export function unwrapEnvelope<T>(res: { data: ApiEnvelope<T> }): T {
  const envelope = res.data as unknown
  if (!envelope || typeof envelope !== 'object') {
    throw new ApiError({
      kind: 'unknown',
      status: null,
      code: null,
      fields: {},
      payload: envelope,
      userVisible: true,
      detail: 'Malformed response envelope',
    })
  }
  const { status, data, message } = envelope as ApiEnvelope<T>
  if (status === 'error') {
    throw new ApiError({
      kind: 'unknown',
      status: null,
      code: null,
      fields: {},
      payload: envelope,
      userVisible: true,
      detail: message ?? 'Request failed',
    })
  }
  return data
}

/** Flat routes (admin, controller, blobs): return `res.data` as-is. */
export function unwrap<T>(res: { data: T }): T {
  return res.data
}

/** Decode an unwrapped DTO while keeping feature API classes free of local error handling. */
export function decodeDto<TInput, TOutput>(
  value: TInput,
  decode: (value: TInput) => TOutput,
): TOutput {
  try {
    return decode(value)
  } catch (cause) {
    throw ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  }
}
